import React, { useState, useRef } from 'react';
import { X, Upload, Type, Image, Video, Palette } from 'lucide-react';
import { storiesService } from '../services/storiesService';
import { supabase } from '../lib/supabase';
import { useLanguage } from '../context/LanguageContext';
import { ContentModerationError } from '../lib/contentModeration';

interface CreateStoryModalProps {
  onClose: () => void;
  onStoryCreated: () => void;
}

const backgroundColors = [
  '#9333ea', '#3b82f6', '#10b981', '#ec4899', '#ef4444',
  '#f97316', '#eab308', '#6366f1', '#14b8a6', '#06b6d4',
  '#8b5cf6', '#f59e0b', '#84cc16', '#06b6d4', '#6366f1'
];

const CreateStoryModal: React.FC<CreateStoryModalProps> = ({ onClose, onStoryCreated }) => {
  const { select, t } = useLanguage();
  const [storyType, setStoryType] = useState<'text' | 'image' | 'video' | null>(null);
  const [textContent, setTextContent] = useState('');
  const [backgroundColor, setBackgroundColor] = useState('#9333ea');
  const [duration, setDuration] = useState(5);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (storyType === 'image' && !file.type.startsWith('image/')) {
      setError(select({ es: 'Por favor selecciona un archivo de imagen válido', en: 'Please select a valid image file' }));
      return;
    }
    
    if (storyType === 'video' && !file.type.startsWith('video/')) {
      setError(select({ es: 'Por favor selecciona un archivo de video válido', en: 'Please select a valid video file' }));
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError(select({ es: 'El archivo no puede superar los 10MB', en: 'File size cannot exceed 10MB' }));
      return;
    }

    setSelectedFile(file);
    setError('');

    // Create preview URL
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
  };

  const uploadFile = async (file: File): Promise<string> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');
    
    return await storiesService.uploadStoryFile(file, user.id);
  };

  const handleSubmit = async () => {
    if (!storyType) return;

    setLoading(true);
    setError('');

    try {
      let contentUrl: string | undefined;

      if (storyType !== 'text' && selectedFile) {
        contentUrl = await uploadFile(selectedFile);
      }

      await storiesService.createStory({
        content_type: storyType,
        content_url: contentUrl,
        text_content: storyType === 'text' ? textContent : undefined,
        background_color: storyType === 'text' ? backgroundColor : undefined,
        duration,
      });

      onStoryCreated();
      onClose();
    } catch (err) {
      if (err instanceof ContentModerationError) {
        const key = err.categories.includes('sexual')
          ? 'moderation.block.sexual'
          : err.categories.includes('profanity')
            ? 'moderation.block.profanity'
            : 'moderation.block.generic';
        setError(t(key, 'Your content violates our community guidelines.'));
      } else {
        setError(err instanceof Error ? err.message : select({ es: 'Error al crear la story', en: 'Error creating story' }));
      }
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = () => {
    if (storyType === 'text') return textContent.trim().length > 0;
    if (storyType === 'image' || storyType === 'video') return selectedFile !== null;
    return false;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">{select({ es: 'Crear Story', en: 'Create Story' })}</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Story Type Selection */}
          {!storyType && (
            <div className="space-y-4">
              <h3 className="font-medium text-gray-900">{select({ es: '¿Qué tipo de story quieres crear?', en: 'What type of story do you want to create?' })}</h3>
              
              <div className="grid grid-cols-1 gap-3">
                <button
                  onClick={() => setStoryType('text')}
                  className="flex items-center gap-3 p-4 border border-gray-300 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-all"
                >
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Type className="text-purple-600" size={20} />
                  </div>
                  <div className="text-left">
                    <h4 className="font-medium text-gray-900">{select({ es: 'Story de Texto', en: 'Text Story' })}</h4>
                    <p className="text-sm text-gray-500">{select({ es: 'Comparte un pensamiento o frase', en: 'Share a thought or quote' })}</p>
                  </div>
                </button>

                <button
                  onClick={() => setStoryType('image')}
                  className="flex items-center gap-3 p-4 border border-gray-300 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-all"
                >
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Image className="text-blue-600" size={20} />
                  </div>
                  <div className="text-left">
                    <h4 className="font-medium text-gray-900">{select({ es: 'Story de Imagen', en: 'Image Story' })}</h4>
                    <p className="text-sm text-gray-500">{select({ es: 'Sube una foto para compartir', en: 'Upload a photo to share' })}</p>
                  </div>
                </button>

                <button
                  onClick={() => setStoryType('video')}
                  className="flex items-center gap-3 p-4 border border-gray-300 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-all"
                >
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Video className="text-green-600" size={20} />
                  </div>
                  <div className="text-left">
                    <h4 className="font-medium text-gray-900">{select({ es: 'Story de Video', en: 'Video Story' })}</h4>
                    <p className="text-sm text-gray-500">{select({ es: 'Comparte un video corto', en: 'Share a short video' })}</p>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Text Story */}
          {storyType === 'text' && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <button
                  onClick={() => setStoryType(null)}
                  className="p-1 hover:bg-gray-100 rounded-full"
                >
                  {select({ es: '← Atrás', en: '← Back' })}
                </button>
                <h3 className="font-medium text-gray-900">{select({ es: 'Story de Texto', en: 'Text Story' })}</h3>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {select({ es: 'Mensaje', en: 'Message' })}
                </label>
                <textarea
                  value={textContent}
                  onChange={(e) => {
                    setError('');
                    setTextContent(e.target.value);
                  }}
                  rows={4}
                  maxLength={200}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                  placeholder={select({ es: '¿Qué quieres compartir?', en: 'What do you want to share?' })}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {textContent.length}/200 {select({ es: 'caracteres', en: 'characters' })}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {select({ es: 'Color de fondo', en: 'Background color' })}
                </label>
                <div className="grid grid-cols-5 gap-2">
                  {backgroundColors.map((color) => (
                    <button
                      key={color}
                      onClick={() => setBackgroundColor(color)}
                      className={`w-12 h-12 rounded-lg border-2 transition-all ${
                        backgroundColor === color
                          ? 'border-gray-800 scale-110'
                          : 'border-gray-300 hover:scale-105'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>

              {/* Preview */}
              <div className="border border-gray-300 rounded-lg overflow-hidden">
                <div className="bg-gray-100 px-3 py-2 text-sm text-gray-600 flex items-center gap-2">
                  <Palette size={14} />
                  {select({ es: 'Vista previa', en: 'Preview' })}
                </div>
                <div 
                  className="aspect-[9/16] flex items-center justify-center p-4 text-center"
                  style={{ backgroundColor }}
                >
                  <p className="text-white text-lg font-bold leading-relaxed">
                    {textContent || select({ es: 'Tu mensaje aparecerá aquí...', en: 'Your message will appear here...' })}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Image/Video Story */}
          {(storyType === 'image' || storyType === 'video') && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <button
                  onClick={() => setStoryType(null)}
                  className="p-1 hover:bg-gray-100 rounded-full"
                >
                  {select({ es: '← Atrás', en: '← Back' })}
                </button>
                <h3 className="font-medium text-gray-900">
                  {select({ es: 'Story de', en: 'Story type:' })} {storyType === 'image' ? select({ es: 'Imagen', en: 'Image' }) : select({ es: 'Video', en: 'Video' })}
                </h3>
              </div>

              {!selectedFile ? (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={storyType === 'image' ? 'image/*' : 'video/*'}
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="flex flex-col items-center gap-3 text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    <Upload size={32} />
                    <div>
                      <p className="font-medium">
                        {storyType === 'image'
                          ? select({ es: 'Haz clic para subir una imagen', en: 'Click to upload an image' })
                          : select({ es: 'Haz clic para subir un video', en: 'Click to upload a video' })}
                      </p>
                      <p className="text-sm">
                        {select({ es: 'Máximo 10MB', en: 'Max 10MB' })} • {storyType === 'image' ? 'JPG, PNG, GIF' : 'MP4, MOV, AVI'}
                      </p>
                    </div>
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Preview */}
                  <div className="border border-gray-300 rounded-lg overflow-hidden">
                    <div className="bg-gray-100 px-3 py-2 text-sm text-gray-600 flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        {storyType === 'image' ? <Image size={14} /> : <Video size={14} />}
                        {select({ es: 'Vista previa', en: 'Preview' })}
                      </span>
                      <button
                        onClick={() => {
                          setSelectedFile(null);
                          setPreviewUrl(null);
                        }}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X size={14} />
                      </button>
                    </div>
                    <div className="aspect-[9/16] bg-black flex items-center justify-center">
                      {storyType === 'image' && previewUrl && (
                        <img
                          src={previewUrl}
                          alt="Preview"
                          className="max-w-full max-h-full object-contain"
                        />
                      )}
                      {storyType === 'video' && previewUrl && (
                        <video
                          src={previewUrl}
                          controls
                          className="max-w-full max-h-full object-contain"
                        />
                      )}
                    </div>
                  </div>

                  <div>
                    <p className="text-sm text-gray-600 mb-2">
                      📁 {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(1)} MB)
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Duration Setting */}
          {storyType && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {select({ es: 'Duración', en: 'Duration' })} ({duration} {select({ es: 'segundos', en: 'seconds' })})
                </label>
                <input
                  type="range"
                  min="3"
                  max="15"
                  value={duration}
                  onChange={(e) => setDuration(parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>3s</span>
                  <span>15s</span>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={onClose}
                  disabled={loading}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  {select({ es: 'Cancelar', en: 'Cancel' })}
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading || !canSubmit()}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
                >
                  {loading ? select({ es: 'Publicando...', en: 'Publishing...' }) : select({ es: 'Publicar Story', en: 'Publish Story' })}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateStoryModal;
